import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shuffle } from 'lucide-react';

/**
 * StepAssignments - Step 4 of VehicleLifecycleWorkflow
 *
 * Allows user to assign:
 * - Organization (required)
 * - Driver (optional, filtered by selected org)
 * - Location/Site (optional, filtered by selected org)
 * - Initial status
 *
 * @param {Object} props
 * @param {import('../VehicleLifecycleWorkflow').VehicleFormData} props.formData - Current form state
 * @param {(field: string, value: any) => void} props.updateFormData - Update a form field
 * @param {Array<{code: string, name: string}>} props.organizations - Available organizations
 * @param {Array<{id: number, first_name: string, last_name: string, license_number: string, org_id: string}>} props.drivers - All drivers (will be filtered by org)
 * @param {Array<{id: number, site_name: string, site_type: string, org_id: string}>} props.sites - All sites (will be filtered by org)
 * @returns {JSX.Element}
 */
function StepAssignments({ formData, updateFormData, organizations, drivers, sites }) {
  const { t } = useTranslation();
  const orgDrivers = drivers.filter(d => d.org_id === formData.org_id);
  const orgSites = sites.filter(s => s.org_id === formData.org_id);

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{t('vehicles.wizard.assignments.title')}</h3>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Shuffle className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
          <div>
            <h4 className="font-semibold text-purple-900 mb-2">{t('vehicles.wizard.assignments.introTitle')}</h4>
            <p className="text-sm text-purple-800">
              {t('vehicles.wizard.assignments.introHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Organization Assignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.assignments.organizationLabel')}</label>
        <select
          value={formData.org_id}
          onChange={(e) => updateFormData('org_id', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        >
          {organizations.map(org => (
            <option key={org.code} value={org.code}>{org.name}</option>
          ))}
        </select>
        <p className="text-sm text-gray-500 mt-2">
          {t('vehicles.wizard.assignments.organizationHint')}
        </p>
      </div>

      {/* Driver Assignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.assignments.driverLabel')}</label>
        {formData.assigned_driver_id && (() => {
          const current = orgDrivers.find(d => String(d.id) === String(formData.assigned_driver_id));
          return current ? (
            <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              {t('vehicles.wizard.assignments.currentDriver')} <span className="font-semibold">{current.first_name} {current.last_name}</span>
            </div>
          ) : null;
        })()}
        <select
          value={formData.assigned_driver_id != null ? String(formData.assigned_driver_id) : ''}
          onChange={(e) => updateFormData('assigned_driver_id', e.target.value ? parseInt(e.target.value) : null)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        >
          <option value="">{t('vehicles.wizard.assignments.noDriverOption')}</option>
          {orgDrivers.map(driver => (
            <option key={driver.id} value={String(driver.id)}>
              {driver.first_name} {driver.last_name} - {driver.license_number}
            </option>
          ))}
        </select>
        <p className="text-sm text-gray-500 mt-2">
          {t('vehicles.wizard.assignments.driverHint')}
        </p>
      </div>

      {/* Location Assignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.assignments.locationLabel')}</label>
        <select
          value={formData.initial_location_id != null ? String(formData.initial_location_id) : ''}
          onChange={(e) => updateFormData('initial_location_id', e.target.value ? parseInt(e.target.value) : null)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        >
          <option value="">{t('vehicles.wizard.assignments.selectLocationOption')}</option>
          {orgSites.map(site => (
            <option key={site.id} value={String(site.id)}>
              {site.site_name} - {site.site_type}
            </option>
          ))}
        </select>
        <p className="text-sm text-gray-500 mt-2">
          {t('vehicles.wizard.assignments.locationHint')}
        </p>
      </div>

      {/* Vehicle Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicles.wizard.assignments.statusLabel')}</label>
        <select
          value={formData.status}
          onChange={(e) => updateFormData('status', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        >
          <option value="active">{t('vehicles.wizard.assignments.statusActiveReady')}</option>
          <option value="maintenance">{t('vehicles.status.maintenance')}</option>
          <option value="out_of_service">{t('vehicles.status.out_of_service')}</option>
        </select>
      </div>
    </div>
  );
}

export default StepAssignments;
